import { useState, useEffect } from 'react';
import { type Auction } from '@chewy/typescript-config/marketplace-types';
import { useEnglishAuctions } from '@/hooks/marketplace/useEnglishAuctions';
import { useConnection } from 'wagmi';
import { readContract } from 'wagmi/actions';
import NFTCard from './NFTCard';
import { nftAbi as erc721Abi } from "@/constants/abi/nftAbi";
import { getTokensByChainId } from '@/constants/marketplace/tokens';
import { formatUnits } from 'viem';
import { config, defaultIpfsGateway } from '@/config/index';
import { type Address } from 'viem';
import { chainSettings, ChainType } from '@/constants/chain-specific';
import { getMarketplaceContract } from '@/constants/marketplace/contracts';
import { getNFTMetadata } from '@/util/nftMetadata';
import { getCollectionByAddress } from '@/constants/marketplace/collections';
interface NFTMetadata {
  name?: string;
  description?: string;
  image?: string;
}

// Define the NFTCardProps type to match the NFTCard component's expectations
type NFTCardData = {
  id: string;
  name: string;
  description: string;
  image: string;
  owner: string;
  tokenId: string;
  assetContractAddress: string;
  collectionName: string;
  chainId: number;
  auction?: {
    formattedMinimumBid: string;
    formattedHighestBid: string;
    currencySymbol: string;
    currencyAddress?: Address;
    endTimeInSeconds: number;
  };
};
interface EnglishAuctionsProps {
  chainId: number;
  collectionAddress?: string;
}
export default function EnglishAuctions({
  chainId,
  collectionAddress
}: EnglishAuctionsProps) {
  const [nftData, setNftData] = useState<NFTCardData[]>([]);
  const [isLoadingNFTs, setIsLoadingNFTs] = useState(false);
  const {
    address
  } = useConnection();
  const {
    auctions,
    loading,
    error
  } = useEnglishAuctions(chainId);
  const marketplaceContract = getMarketplaceContract(chainId);
  const nativeSymbol = chainSettings[chainId as ChainType]?.nativeCurrency.symbol || 'ETH';

  // Filter auctions by collection address if provided
  const filteredAuctions = collectionAddress ? auctions.filter(auction => auction.assetContract.toLowerCase() === collectionAddress.toLowerCase()) : auctions;

  // Get token metadata for active auctions
  useEffect(() => {
    const fetchNFTData = async () => {
      if (filteredAuctions.length === 0) return;
      setIsLoadingNFTs(true);
      try {
        const nftPromises = filteredAuctions.map(async auction_0 => {
          // Get token currency info
          const supportedTokens = getTokensByChainId(chainId);
          const currencyInfo = supportedTokens?.tokens.find(token => token.address.toLowerCase() === auction_0.currency.toLowerCase());

          // Get NFT metadata using proper metadata fetching system
          try {
            // Get collection info to determine token type
            const collection = getCollectionByAddress(auction_0.assetContract as Address, chainId);

            // Use tokenType from marketplace auction data (0 = ERC721, 1 = ERC1155)
            // This is more reliable than collection config since it comes from the contract
            const isERC1155 = auction_0.tokenType === 1;

            // Use the proper metadata fetching utility
            const metadata = await getNFTMetadata(auction_0.assetContract as Address, auction_0.tokenId, chainId, {
              useCache: true,
              skipCollectionConfig: false,
              isERC1155
            });

            // Get collection name
            let name = collection?.name || (await readContract(config, {
              address: auction_0.assetContract as Address,
              abi: erc721Abi,
              functionName: 'name',
              args: [],
              chainId
            }));

            // Use short address if collection name is unknown
            if (name === 'Unknown Collection' && auction_0.assetContract) {
              const shortAddress = `${auction_0.assetContract.substring(0, 6)}...${auction_0.assetContract.substring(auction_0.assetContract.length - 4)}`;
              name = `Collection ${shortAddress}`;
            }

            // Format price based on token decimals
            const decimals_0 = currencyInfo?.decimals || 18;
            const formattedMinimumBid_0 = formatUnits(auction_0.minimumBidAmount, decimals_0);
            // If highestBid is available, use it, otherwise use minimum bid
            // Use auction.buyoutBidAmount if available and > 0, otherwise use minimumBidAmount
            const formattedHighestBid_0 = auction_0.buyoutBidAmount && auction_0.buyoutBidAmount > 0n ? formatUnits(auction_0.buyoutBidAmount, decimals_0) : formattedMinimumBid_0;
            return {
              id: `${auction_0.assetContract}-${auction_0.tokenId}`,
              name: metadata.name || `${name} #${auction_0.tokenId}`,
              description: metadata.description || '',
              image: metadata.image?.replace('ipfs://', defaultIpfsGateway) || '/placeholder.png',
              owner: auction_0.auctionCreator,
              tokenId: String(auction_0.tokenId),
              assetContractAddress: auction_0.assetContract,
              collectionName: String(name) || 'Unknown Collection',
              chainId,
              auction: {
                formattedMinimumBid: formattedMinimumBid_0,
                formattedHighestBid: formattedHighestBid_0,
                currencySymbol: currencyInfo?.symbol || nativeSymbol,
                currencyAddress: auction_0.currency as Address,
                endTimeInSeconds: Number(auction_0.endTimestamp)
              }
            } as NFTCardData;
          } catch (err_0) {
            console.error(`Error fetching data for token ${auction_0.tokenId}:`, err_0);

            // Instead of returning null, return a basic card with available auction info
            // This ensures users still see the auction even if metadata can't be loaded
            const decimals = currencyInfo?.decimals || 18;
            const formattedMinimumBid = formatUnits(auction_0.minimumBidAmount, decimals);
            // Use auction.buyoutBidAmount if available and > 0, otherwise use minimumBidAmount
            const formattedHighestBid = auction_0.buyoutBidAmount && auction_0.buyoutBidAmount > 0n ? formatUnits(auction_0.buyoutBidAmount, decimals) : formattedMinimumBid;
            return {
              id: `${auction_0.assetContract}-${auction_0.tokenId}`,
              name: `Token #${auction_0.tokenId}`,
              description: 'Metadata unavailable',
              image: '/placeholder.png',
              owner: auction_0.auctionCreator,
              tokenId: String(auction_0.tokenId),
              assetContractAddress: auction_0.assetContract,
              collectionName: 'Unknown Collection',
              chainId,
              auction: {
                formattedMinimumBid,
                formattedHighestBid,
                currencySymbol: currencyInfo?.symbol || nativeSymbol,
                currencyAddress: auction_0.currency as Address,
                endTimeInSeconds: Number(auction_0.endTimestamp)
              }
            } as NFTCardData;
          }
        });
        const results = await Promise.all(nftPromises);
        setNftData(results.filter(Boolean) as NFTCardData[]);
      } catch (err) {
        console.error('Error fetching NFT data:', err);
      } finally {
        setIsLoadingNFTs(false);
      }
    };
    fetchNFTData();
  }, [filteredAuctions, chainId]);

  // Generate placeholder for loading state
  const generatePlaceholder = (id: string): NFTCardData => ({
    id,
    name: '',
    description: '',
    image: '',
    owner: '',
    tokenId: '',
    assetContractAddress: '',
    collectionName: '',
    chainId: 0
  });
  if (loading) {
    return <div className='py-4'>
        <div className='grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6'>
          {Array.from({
          length: 8
        }, (_, i) => <NFTCard key={`loading-placeholder-${i}`} nft={generatePlaceholder(`loading-placeholder-${i}`)} isLoading={true} />)}
        </div>
      </div>;
  }
  if (error) {
    return <div className='py-4 text-center text-red-500'>
        Error loading auctions: {error.message}
      </div>;
  }
  if (!loading && filteredAuctions.length === 0) {
    return <div className='py-12 text-center text-gray-400'>
        <p>No active auctions found</p>
      </div>;
  }
  return <div className='py-4'>
      <div className='grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6'>
        {isLoadingNFTs ? Array.from({
        length: filteredAuctions.length
      }, (__0, i_0) => <NFTCard key={`loading-auction-${i_0}`} nft={generatePlaceholder(`loading-auction-${i_0}`)} isLoading={true} />) : nftData.map(nft => <NFTCard key={nft.id} nft={nft} />)}
      </div>
    </div>;
}